import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useListQueryParams } from "hooks";
import {
  SearchTextParamName,
  SortDirection,
  Website,
  WebsiteTypeKeys,
} from "types";
import {
  FilterField,
  filterEntities,
  filterEntityByField,
  getComparisonType,
  getSearchFields,
  getSortFields,
  sortEntities,
  sortEntitiesByField,
} from "utils/tables";

const searchFields = getSearchFields<Website>(WebsiteTypeKeys);

type WebsiteListParams = {
  templateID: string;
};

export const useWebsiteListFilterAndSort = (filter = true) => {
  const searchParams = useListQueryParams();

  const { templateID } = useParams<WebsiteListParams>();

  const filterFields: FilterField<Website>[] = useMemo(() => {
    const defaultFilters: Partial<Record<keyof Website, string>> = {};

    if (templateID) {
      defaultFilters.templateID = templateID;
    }

    const filters: FilterField<Website>[] = Array.from(
      searchParams.entries(),
    ).map(([field, value]) => ({
      field: field as keyof Website,
      value,
      comparisonType: getComparisonType(searchParams, field),
    }));

    const defaultFilterFields: FilterField<Website>[] = Object.entries(
      defaultFilters,
    )
      .filter(
        ([key, value]) =>
          value !== undefined &&
          !filters.some((filterField) => filterField.field === key),
      )
      .map(([field, value]) => ({
        field: field as keyof Website,
        value,
        comparisonType: getComparisonType(searchParams, field),
      }));

    const allFilters = [...filters, ...defaultFilterFields];

    return allFilters;
  }, [searchParams, templateID]);

  const sortFields = useMemo(() => {
    const defaultSorts: Partial<Record<keyof Website, SortDirection>> = {
      url: SortDirection.asc,
    };

    return getSortFields<Website>(searchParams.get("sort"), defaultSorts);
  }, [searchParams]);

  const filterAndSortWebsites = useCallback(
    (
      websiteList: Website[],
      customSortFunctions: Partial<
        Record<
          keyof Website,
          (a: Website, b: Website, direction: SortDirection) => number
        >
      > = {},
      customFilterFunctions: Partial<
        Record<keyof Website, (entity: Website, value: string) => boolean>
      > = {},
    ) =>
      sortEntities<Website>(
        filter
          ? filterEntities<Website>(
              websiteList,
              filterFields,
              searchFields,
              searchParams.get(SearchTextParamName.SearchWebsite),
              filterEntityByField<Website>,
              WebsiteTypeKeys,
              customFilterFunctions,
            )
          : websiteList,
        sortFields,
        sortEntitiesByField<Website>,
        WebsiteTypeKeys,
        customSortFunctions,
      ).map((website) => ({
        ...website,
        updatedAt: new Date(
          new Date(website.updatedAt ?? new Date().getTime()).getTime() + 1000,
        ).toISOString(),
      })),
    [filter, filterFields, searchParams, sortFields],
  );

  return filterAndSortWebsites;
};
